import { DataAccess } from "../../dataaccess/data.access";
import { Cache, Invalidate } from "../../decorators/cache.decorator";
import { Timeout } from "../../decorators/timeout.decorator";
import { BpeUtil } from "./bpe.util";
import { S25Util } from "../../util/s25-util";
import { EmailClass, EmailReport, EmailService, TodoClass } from "../../services/email.service";
import { ExecutionEnvironment } from "../../services/execution.enviroment";
import { TodoService } from "../../services/task/todo.service";
import { FlsService } from "../../services/fls.service";
import { Proto } from "../../pojo/Proto";
import { EventData, EventIncludeOption, EventService } from "../../services/event.service";
import { PricingService } from "../../services/pricing.service";
import { Adjustment, LineItem, Total } from "../../pojo/Pricing";
import { TaskService } from "../../services/task/task.service";
import { AccessLevels, Fls, hasFullFls, hasReadFls } from "../../pojo/Fls";
import { UserprefService } from "../../services/userpref.service";
import { Bpe } from "./BpeI";
import { DocumentListItem, DocumentService } from "../../services/document.service";
import { Rules } from "../s25-rule-tree/s25.rule.const";
import { TelemetryService } from "../../services/telemetry.service";
import { MasterDefTag } from "../../pojo/MasterDefTag";
import { MasterDefinitionTagsService } from "../../services/master.definitions/master.definition.tags.service";
import { ScheduledEmailService } from "../../services/scheduled.email.service";
import { Item } from "../../pojo/Item";
import { OrganizationService } from "../../services/organization.service";
import { DefinitelyArray } from "../../pojo/Util";
import { Report } from "../../pojo/Report";
import { Task } from "../../pojo/Task";
import * as Sentry from "@sentry/angular";
import { PushNotifyService } from "./push.notify.service";
import { SpaceService } from "../../services/space.service";
import NumericalBoolean = Proto.NumericalBoolean;
import NumericalString = Proto.NumericalString;
import MdTag = MasterDefTag.MdTag;
import BpeScenarioTypes = Bpe.BpeScenarioTypes;
import BpeVars = Bpe.BpeVars;
import BpeVarsCombined = Bpe.BpeVarsCombined;
import { CustomAttributes } from "../../pojo/CustomAttributes";

export class BpeService {
    public static isDebugInstance() {
        //Troubleshooting ANG-5908
        return ["gordon", "burnside"].includes(window.ProData?.instanceId);
    }

    @Timeout
    @Cache({ immutable: true, targetName: "BpeService" })
    public static getFullTemplates() {
        return DataAccess.get(DataAccess.injectCaller("/bpe/templates.json", "BpeService.getFullTemplates")).then(
            function (data: EmailFullTemplatesResponse) {
                let templates = data?.root?.templates || [];
                templates.forEach((template) => {
                    template.tags = S25Util.array.forceArray(template.tags);
                    //ANG-4975 we found 'Confirmation' instead of 'confirmation' once. Force lowercase here instead of tracking down every comparison
                    //also normalizes a single report eg. 423 to return as a string instead of a number
                    template.reports = S25Util.toStr(template.reports)?.toLowerCase();
                });
                return templates;
            },
        );
    }

    /**
     * @param getAll - if true will override tag base security
     * @param omitReadOnly
     */
    @Timeout
    @Cache({ immutable: true, targetName: "BpeService" })
    public static async getManualTemplates(getAll = false, omitReadOnly?: boolean) {
        let data: EmailTemplate[];
        if (getAll) {
            data = await BpeService.getFullTemplates();
            data = data.filter(
                (template) =>
                    S25Util.toBool(template.isManual) &&
                    (!omitReadOnly || MasterDefinitionTagsService.isItemReadOnly(template.tags)),
            );
        } else {
            //using type_name=manual will only return user can see - configured via system tags
            const resp = await DataAccess.get(
                DataAccess.injectCaller("/bpe/templates.json?type_name=manual", "BpeService.getManualTemplates"),
            );
            data = resp?.root?.templates || [];
        }

        return data;
    }

    @Timeout
    @Cache({ immutable: true, targetName: "BpeService" })
    public static getFullTodoTemplates() {
        return DataAccess.get(
            DataAccess.injectCaller("/bpe/templates.json?type_name=todo", "BpeService.getFullToDoTemplates"),
        ).then(function (data: ToDoFullTemplatesResponse) {
            return (data && data.root && data.root.templates) || [];
        });
    }

    @Timeout
    @Cache({ immutable: true, targetName: "BpeService" })
    public static getFullScenarios() {
        return DataAccess.get(DataAccess.injectCaller("/bpe/scenarios.json", "BpeService.getFullScenarios")).then(
            function (data: EmailFullScenariosResponse) {
                return (data && data.root && data.root.scenarios) || [];
            },
        );
    }

    public static async getSystemScenarios(): Promise<EmailScenario[]> {
        const newTaskPush = {
            code:
                "// DATA " +
                'const $on                    = "all"; // Can be "all", "create", or "edit"\n' +
                'const $sources               = ["task","event-form","express","event-state-change","createTodo"]; // Trigger on changes to these\n' +
                'const $tasksCompleteTriggers = []; // Can be "Assignments" (AP), "Approvals" (NP), or "FYIs" (NP) or "To Dos"\n' +
                'const $taskActionTriggers = {"taskTypes":["Assign","UnAssign","Authorization","FYI","ToDo"],"actions":["create"]}; ',
            onEventStateChangeAction: 1,
            onEventFormAction: 1,
            isScheduled: 0,
            isActive: 1,
            mode: "hybridForm",
            itemId: 122,
            onAction: "all",
            inclResPref: 0,
            allApDone: 0,
            itemName: "Push notification on new task",
            onTaskAction: 0,
            onTodoAction: 0,
            onExpressAction: 1,
            allNpDone: 0,
            inclLocPref: 0,
            allFyiDone: 0,
            allTodoDone: 0,
            createToDo: 0,
            pushNotify: 1, //indicates we'll send a push notify
        } as EmailScenario;

        return [newTaskPush];
    }

    @Timeout
    @Invalidate({ serviceName: "BpeService", methodName: "getManualTemplates" })
    @Invalidate({ serviceName: "BpeService", methodName: "getFullTemplates" })
    @Invalidate({ serviceName: "BpeService", methodName: "getFullScenarios" })
    @Invalidate({ serviceName: "BpeService", methodName: "getFullTodoTemplates" })
    public static putTemplate(
        itemId: number,
        itemName: string,
        isManual: boolean,
        mode: any,
        to: string[],
        from?: string,
        cc?: string[],
        bcc?: string[],
        reports?: string[],
        subject?: string,
        body?: string,
        code?: any,
    ) {
        return DataAccess.put(DataAccess.injectCaller("/bpe/template.json", "BpeService.putTemplate"), {
            root: {
                itemId: itemId,
                itemName: itemName,
                isManual: +isManual, //convert boolean to number
                mode: mode,
                to: to,
                from: from,
                cc: cc,
                bcc: bcc,
                reports: reports,
                subject: subject,
                body: body,
                code: code,
            },
        });
    }

    @Timeout
    @Invalidate({ serviceName: "BpeService", methodName: "getFullTemplates" })
    @Invalidate({ serviceName: "BpeService", methodName: "getFullScenarios" })
    @Invalidate({ serviceName: "BpeService", methodName: "getFullTodoTemplates" })
    public static putTemplateByTemplate(template: any) {
        if (template.isToDoTemplate && template.isToDoTemplate === 1) {
            const { itemId, itemName, mode, taskName, assignBy, assignTo, comment, dueDate, code } = template;
            return DataAccess.put(
                DataAccess.injectCaller("/bpe/template.json?type_name=todo", "BpeService.putTemplate"),
                {
                    root: {
                        itemId,
                        itemName,
                        mode,
                        taskName,
                        assignBy,
                        assignTo,
                        comment,
                        dueDate,
                        code,
                    },
                },
            );
        } else {
            const { itemId, itemName, isManual, iCalFile, mode, to, from, cc, bcc, reports, subject, body, code } =
                template;
            return DataAccess.put(DataAccess.injectCaller("/bpe/template.json", "BpeService.putTemplate"), {
                root: {
                    itemId,
                    itemName,
                    isManual,
                    iCalFile,
                    mode,
                    to,
                    from,
                    cc,
                    bcc,
                    reports,
                    subject,
                    body,
                    code,
                },
            });
        }
    }

    @Timeout
    @Invalidate({ serviceName: "BpeService", methodName: "getFullScenarios" })
    public static putScenario(
        itemId: string,
        itemName: string,
        mode: string,
        isActive: boolean,
        inclLocPref: boolean,
        inclResPref: boolean,
        onAction: any,
        onTaskAction: any,
        onEventFormAction: any,
        onExpressAction: any,
        onEventStateChangeAction: any,
        onTodoAction: boolean,
        eventStates: any,
        preEventStates: any,
        eventTypes: any,
        locations: any,
        resources: any,
        organizations: any,
        attributes: any,
        requirements: any,
        securityGroups: any,
        code: any,
        templates: any,
    ) {
        let data: any = {
            root: {
                itemId: itemId || "",
                itemName: itemName || "",
                mode: mode,
                isActive: +isActive, //convert boolean to int
                inclLocPref: +inclLocPref,
                inclResPref: +inclResPref,
                onAction: onAction,
                onTaskAction: +onTaskAction,
                onEventFormAction: +onEventFormAction,
                onExpressAction: +onExpressAction,
                onEventStateChangeAction: +onEventStateChangeAction,
                onTodoAction: +onTodoAction,
                eventStates: eventStates,
                preEventStates: preEventStates,
                eventTypes: eventTypes,
                locations: locations,
                resources: resources,
                organizations: organizations,
                attributes: attributes,
                requirements: requirements,
                securityGroups: securityGroups,
                code: code,
                templates: templates,
            },
        };

        return DataAccess.put(DataAccess.injectCaller("/bpe/scenario.json", "BpeService.putScenario"), data);
    }

    @Timeout
    @Invalidate({ serviceName: "BpeService", methodName: "getFullScenarios" })
    public static putScenarioByScenario(scenario: EmailScenario) {
        if (scenario.isScheduled) {
            TelemetryService.sendWithSub("EventSaveEmail", "Scenario", "AddSchedule");
        }
        return DataAccess.put(DataAccess.injectCaller("/bpe/scenario.json", "BpeService.putScenarioByScenario"), {
            root: scenario,
        });
    }

    @Timeout
    @Invalidate({ serviceName: "BpeService", methodName: "getManualTemplates" })
    @Invalidate({ serviceName: "BpeService", methodName: "getFullTemplates" })
    @Invalidate({ serviceName: "BpeService", methodName: "getFullScenarios" })
    @Invalidate({ serviceName: "BpeService", methodName: "getFullTodoTemplates" })
    public static deleteTemplate(itemId: number, type?: any) {
        if (type && type === "todo") {
            return DataAccess.delete(
                DataAccess.injectCaller(
                    "/bpe/template.json?itemId=" + itemId + "&type_name=todo",
                    "BpeService.deleteTemplate",
                ),
            );
        } else {
            return DataAccess.delete(
                DataAccess.injectCaller("/bpe/template.json?itemId=" + itemId, "BpeService.deleteTemplate"),
            );
        }
    }

    @Timeout
    @Invalidate({ serviceName: "BpeService", methodName: "getFullScenarios" })
    public static deleteScenario(itemId: number) {
        return DataAccess.delete(
            DataAccess.injectCaller("/bpe/scenario.json?itemId=" + itemId, "BpeService.deleteScenario"),
        );
    }

    @Timeout
    public static async getInterpolatedString(
        eventData: any,
        profileId: number,
        preEventData: any,
        str: any,
        skipNestedStr?: boolean,
        source?: "document" | "scenario" | "template",
        vars?: Bpe.BpeVars,
    ) {
        str = S25Util.toStr(str);
        if (!vars) {
            vars = await BpeUtil.getVariables(eventData, profileId, preEventData, null);
        }
        return {
            interpolatedStr: BpeUtil.interpolateString(vars, str, skipNestedStr, null, source),
            vars: vars,
        };
    }

    @Timeout
    //Pre event data is optional for use with manual templates
    public static async getEmailObjectFromTemplate(
        eventData: any,
        profileId: number,
        template: any,
        preEventData?: any,
    ) {
        let templateCode = template.mode === "code" ? template.code : BpeUtil.templateToCode(template);
        let vars = await BpeUtil.getVariables(eventData, profileId, preEventData, null);
        vars = await BpeService.getConditionalVariables(eventData, [templateCode], vars);
        const resp = await BpeService.getInterpolatedString(
            eventData,
            profileId,
            preEventData,
            `{${templateCode}}`,
            null,
            null,
            vars,
        );
        try {
            return ExecutionEnvironment.run(resp.interpolatedStr, {
                vars: resp.vars,
                eventData: eventData,
                util: BpeUtil.util,
            });
        } catch (e) {
            BpeService.isDebugInstance() && Sentry.captureException(e);
            console.error(S25Util.errorText(e));
            return null;
        }
    }

    public static scenarioPreData: Map<number, Promise<EventData>> = new Map();
    public static runningScenarios = {
        initialized: new Set<RunScenariosData<unknown>>(),
        readyForPost: new Set<RunScenariosData<unknown>>(),
        fetchingPost: new Set<RunScenariosData<unknown>>(),
        triggering: new Set<RunScenariosData<unknown>>(),
    };
    public static areScenariosRunning() {
        return Object.values(BpeService.runningScenarios ?? {}).some((set) => !!set.size);
    }

    public static setScenarioRunState(
        newState: keyof typeof BpeService.runningScenarios,
        run: RunScenariosData<unknown>,
    ) {
        for (const set of Object.values(BpeService.runningScenarios)) set.delete(run);
        BpeService.runningScenarios[newState].add(run);
    }

    public static endScenarioRun(run: RunScenariosData<unknown>) {
        for (const set of Object.values(BpeService.runningScenarios)) set.delete(run);
    }

    public static async runScenarios<T>(run: RunScenariosData<T>): Promise<T> {
        if (run.silent) return run.change();

        try {
            await BpeService.initializeScenarioRun(run);

            // We need to check that promises for event IDs actually settle
            let eventIdSettled = false;
            if (typeof run.eventId === "number") eventIdSettled = true;
            else run.eventId.finally(() => (eventIdSettled = true));

            const returnData = await run.change();

            if (!eventIdSettled) {
                // Without an event ID we can't run scenarios
                BpeService.endScenarioRun(run);
                return returnData;
            }

            BpeService.setScenarioRunState("readyForPost", run);
            BpeService.scenarioRunsFetchPost(); // Caller does not need to wait for scenarios to finish
            return returnData;
        } catch (error) {
            BpeService.endScenarioRun(run);
            throw error;
        }
    }

    public static async initializeScenarioRun<T>(run: RunScenariosData<T>) {
        BpeService.setScenarioRunState("initialized", run);
        // Fetch pre-data if we have an eventID
        let preData: EventData;
        if (typeof run.eventId === "string") run.eventId = Number(run.eventId);
        if (typeof run.eventId === "number") {
            if (!BpeService.scenarioPreData.has(run.eventId)) {
                const promise = BpeService.getEventData(run.eventId);
                BpeService.scenarioPreData.set(run.eventId, promise);
            }
            preData = await BpeService.scenarioPreData.get(run.eventId);
        } else {
            // Event ID is a promise; set pre-data to undefined when it resolves in case there's somehow other
            // changes with this ID
            run.eventId.then((id) => BpeService.scenarioPreData.set(id, undefined)).catch(() => {});
        }
        return preData;
    }

    public static async scenarioRunsFetchPost() {
        // Make sure that all scenario runs are ready to fetch post data
        if (!!BpeService.runningScenarios.initialized.size) return;

        for (const run of BpeService.runningScenarios.readyForPost) {
            BpeService.setScenarioRunState("fetchingPost", run);
        }
        const preData = BpeService.scenarioPreData;
        BpeService.scenarioPreData = new Map();

        // Get unique event IDs
        const eventIds: Set<number> = new Set();
        for (const run of BpeService.runningScenarios.fetchingPost) {
            const [eventId, err] = await S25Util.Maybe(run.eventId);
            if (!!err || !eventId) BpeService.endScenarioRun(run);
            else eventIds.add(Number(eventId));
        }

        // Fetch post data
        const postData: Map<number, EventData> = new Map();
        const postDataPromises: Promise<void>[] = [];
        for (const eventId of eventIds) {
            const pre = await preData.get(eventId);
            const promise = BpeService.getPostEventData(eventId, pre)
                .then((eventData) => postData.set(eventId, eventData))
                .catch(() => {}); // If there's an error just don't set post-data
            postDataPromises.push(<Promise<void>>promise);
        }
        await Promise.allSettled(postDataPromises);

        await BpeService.scenarioRunsTrigger(preData, postData);
    }

    public static async scenarioRunsTrigger(
        preData: Map<number, Promise<EventData>>,
        postData: Map<number, EventData>,
    ) {
        // Make sure that all runs are ready to trigger
        if (!!BpeService.runningScenarios.initialized.size || !!BpeService.runningScenarios.readyForPost.size) return;

        const runs = Array.from(BpeService.runningScenarios.fetchingPost);
        for (const run of runs) BpeService.setScenarioRunState("triggering", run);

        // Organize runs by event
        const byEvent: Map<number, RunScenariosData<unknown>[]> = new Map();
        for (const run of runs) {
            const eventId = Number(await run.eventId);
            if (!byEvent.has(eventId)) byEvent.set(eventId, []);
            byEvent.get(eventId).push(run);
        }

        // Run scenarios on each event
        const promises: Promise<void>[] = [];
        for (const [eventId, runs] of byEvent) {
            const pre = await preData.get(eventId);
            const post = postData.get(eventId);
            if (!post) continue;

            // Get triggered scenarios
            const email: EmailScenario[] = [];
            const schedule: EmailScenario[] = [];
            const todo: EmailScenario[] = [];
            const pushNotify: EmailScenario[] = [];
            for (const run of runs) {
                const sources = run.getSources(pre, post) || [];
                for (const source of sources) {
                    const triggered = await BpeService.getTriggeredScenarios({
                        source: source,
                        preEventData: pre,
                        postEventData: post,
                        updateScheduledEmails: run.updateScheduledEmails,
                    });
                    if (triggered?.email?.length) email.push(...triggered.email);
                    if (triggered?.schedule?.length) schedule.push(...triggered.schedule);
                    if (triggered?.todo?.length) todo.push(...triggered.todo);
                    if (triggered?.pushNotify?.length) pushNotify.push(...triggered.pushNotify);
                }
            }

            // Profile ID is only used with manual emails
            const variables = await BpeUtil.getVariables(post, undefined, pre);

            promises.push(BpeService.sendEmails(email, variables, post));
            promises.push(BpeService.scheduleEmails(schedule, variables, post));
            promises.push(BpeService.bpeCreateToDo(todo, variables, post));

            // Does not process a template, instead it finds the contact id for each task
            promises.push(BpeService.sendPushNotify(pushNotify, variables as BpeVarsCombined, post));
        }

        await Promise.allSettled(promises);
        for (const run of runs) BpeService.endScenarioRun(run);
    }

    public static async getDataForOrganizations(orgs: DefinitelyArray<EventData["organization"]>) {
        if (!orgs?.length) return;

        const orgById = new Map(orgs.map((org) => [org.organization_id, org]));
        const ids = orgs.map((org) => org.organization_id);

        const orgData = await OrganizationService.getOrganizationsById(ids, ["contacts", "address"]);

        for (const org of orgData) {
            const eventOrg = orgById.get(Number(org.organization_id));
            if (!eventOrg) continue;
            eventOrg.contact = org.contact;
            eventOrg.address = org.address;
        }
    }

    // Get organization addresses and contacts from preEventData, then marshall it to postEventData
    // If postEventData has organizations which preEventData does not have, fetch those too, afterward
    public static async getAdditionalEventDataOrganizations(data: {
        preEventData?: EventData;
        postEventDataPromise: Promise<EventData>;
    }) {
        const { preEventData, postEventDataPromise } = data;

        const preOrgs = S25Util.array.forceArray(preEventData?.organization);
        const preOrgsById = new Map(preOrgs.map((org) => [org.organization_id, org]));
        await BpeService.getDataForOrganizations(preOrgs);

        // Apply data from preEvent orgs to postEvent orgs
        const postEventData = await postEventDataPromise;
        if (!postEventData) return;
        const postOrgs = S25Util.array.forceArray(postEventData.organization);
        const missingOrgs: DefinitelyArray<EventData["organization"]> = [];
        for (const org of postOrgs) {
            const preOrg = preOrgsById.get(org.organization_id);
            if (!preOrg) missingOrgs.push(org);
            else {
                org.contact = preOrg.contact;
                org.address = preOrg.address;
            }
        }

        // Fetch any missing data
        await BpeService.getDataForOrganizations(missingOrgs);
    }

    // Use preEventData to get scheduled emails and attach to both pre- and postEvent data
    public static async getAdditionalEventDataScheduledEvents(data: {
        preEventData?: EventData;
        postEventDataPromise: Promise<EventData>;
    }) {
        const { preEventData, postEventDataPromise } = data;
        if (!preEventData) return; // If there is no preEventData there can be no scheduled emails

        const eventId = Number(preEventData.event_id);
        const [emails, postEventData] = await Promise.all([
            ScheduledEmailService.getScheduledEmails(Item.Ids.Event, eventId),
            postEventDataPromise,
        ]);
        preEventData.scheduledEmails = emails;
        if (postEventData) postEventData.scheduledEmails = emails;
    }

    public static async getPostEventData(eventId: number, preData: EventData | undefined): Promise<EventData> {
        const postData = BpeService.getEventData(eventId);
        await BpeService.getAdditionalEventData({
            preEventData: preData,
            postEventDataPromise: postData,
        });
        return postData;
    }

    public static async getAdditionalEventData(data: {
        preEventData?: EventData;
        postEventDataPromise: Promise<EventData>;
    }) {
        await Promise.all([
            BpeService.getAdditionalEventDataOrganizations(data),
            BpeService.getAdditionalEventDataScheduledEvents(data),
        ]);
    }

    /**
     * Determines which scenarios have had their criteria met, so they are triggered.
     * Returns - The vars object created from Pre/Post save eventData and a list of returned scenarios
     * @param data
     */
    @Timeout
    public static async getTriggeredScenarios(data: {
        updateScheduledEmails?: boolean;
        source: BpeScenarioTypes;
        preEventData: EventData;
        postEventData: EventData;
        skipSystemScenarios?: boolean;
    }): Promise<{
        variables: BpeVars;
        todo: EmailScenario[];
        email: EmailScenario[];
        schedule: EmailScenario[];
        pushNotify: EmailScenario[];
    }> {
        const { preEventData, postEventData, source } = data;
        let getFullTaskData = false;
        let todoId;

        //Only care about cancel request todos, other todos will get email from the admin/db templates only
        if ("cancelTodos" === source) {
            const changedTodos = TodoService.findChangedTodos(findTodos(preEventData), findTodos(postEventData));
            if (changedTodos.new.length > 0 || changedTodos.changed.length > 0) {
                todoId = changedTodos.new.length ? changedTodos.new[0] : changedTodos.changed[0];
                getFullTaskData = true;
            } else {
                return;
            }
        }

        const [scenarios, variables, systemScenarios] = await Promise.all([
            BpeService.getFullScenarios(),
            BpeUtil.getVariables(postEventData, undefined, preEventData, source, getFullTaskData, todoId),
            BpeService.getSystemScenarios(),
        ]);

        const completeScenarios = data.skipSystemScenarios ? scenarios : scenarios.concat(systemScenarios);

        const triggeredScenarios: Set<number> = new Set();
        const todo: EmailScenario[] = [];
        const email: EmailScenario[] = [];
        const schedule: EmailScenario[] = [];
        const pushNotify: EmailScenario[] = [];
        for (const scenario of completeScenarios) {
            const triggered = BpeService.executeScenario({ scenario, variables, eventData: postEventData });
            if (!triggered) continue;

            triggeredScenarios.add(scenario.itemId);
            if (scenario.createToDo) todo.push(scenario);
            if (scenario.pushNotify) pushNotify.push(scenario); //Scenario might do multiple actions, createTodo, push notify etc
            // Note that a scenario can BOTH create a task AND send emails, so the below should not be an else if
            if (scenario.templates?.length) {
                if (scenario.isScheduled) schedule.push(scenario);
                else email.push(scenario);
            }

            if (!window.unitTesting) console.log("scenario will run", scenario.itemName);
        }

        // We need to update any scheduled emails, even if the scenario did not trigger
        // If no change was made (e.g. user acted on an FYI task), we don't need to update any scheduled emails
        if (data.updateScheduledEmails ?? true) {
            const scenariosById = new Map(scenarios.map((s) => [s.itemId, s]));
            for (const scheduledEmail of postEventData.scheduledEmails ?? []) {
                const id = scheduledEmail.scenarioId;
                if (!id) continue;
                const triggered = triggeredScenarios.has(id);
                if (!triggered) schedule.push(scenariosById.get(id));
            }
        }

        //This comment is to make grabbing test scenarios more simple
        // console.log("mockData", {
        //     source: source,
        //     profileId: profileId,
        //     eventData: eventData,
        //     preEventData: preEventData,
        //     mockedScenarios:
        //         resp?.scenarios?.length > 0 &&
        //         resp.scenarios.filter((sec: any) => {
        //             return sec?.isActive === 1;
        //         }),
        //     results: retVal,
        // });

        return {
            variables,
            todo,
            email,
            schedule,
            pushNotify,
        };
    }

    public static executeScenario(data: { scenario: EmailScenario; variables: BpeVars; eventData: any }): boolean {
        const { scenario, variables, eventData } = data;
        if (!S25Util.toBool(scenario.isActive)) return false;

        const scenarioCode = /code/i.test(scenario.mode) ? scenario.code : BpeUtil.scenarioToCode(scenario);
        const finalScenarioCode = `{${BpeUtil.interpolateString(variables, scenarioCode)}}`; // Wrapped in {} to keep variables out of global scope
        variables.scenario = scenario.itemName;
        try {
            return ExecutionEnvironment.run(finalScenarioCode, {
                vars: variables,
                eventData,
                util: BpeUtil.util,
            });
        } catch (e) {
            BpeService.isDebugInstance() && Sentry.captureException(e);
            console.error(S25Util.errorText(e));
            return false;
        }
    }

    /**
     * Currently an alias for BpeService.sendEmails
     */
    public static async scheduleEmails(scenarios: EmailScenario[], variables: BpeVars, eventData: any) {
        scenarios = S25Util.array.unique(scenarios, (s) => s.itemId);
        return BpeService.sendEmails(scenarios, variables, eventData);
    }

    //source: "event-form", "express", "event-state-change", "tasks", "cancelTodos"
    @Timeout
    public static async sendEmails(scenarios: EmailScenario[], variables: BpeVars, eventData: EventData) {
        if (!scenarios.length) return;
        scenarios = S25Util.array.unique(scenarios, (s) => s.itemId);
        let eventId = parseInt(S25Util.propertyGetVal(eventData, "event_id"));

        const templates = S25Util.array.flatten(scenarios.map((s) => s.templates));
        variables = await BpeService.getConditionalVariables(
            eventData,
            templates.map(BpeUtil.templateToCode),
            variables,
        );

        const [fls, sessionId, reports, contracts] = await Promise.all([
            FlsService.getFls(),
            UserprefService.getSessionId(),
            EventService.getEventTypeReports(),
            DocumentService.getDocumentList("event"),
        ]);

        const sendData: BpeSendEmailData = { eventData, eventId, variables, fls, sessionId, reports, contracts };
        for (const scenario of scenarios || []) {
            for (const template of scenario?.templates || []) {
                BpeService.sendTemplate({ ...sendData, template, scenario }).catch((err) =>
                    console.error(S25Util.errorText(err)),
                );
            }
        }
    }

    public static async getConditionalVariables(
        eventData: EventData,
        templates: string[],
        vars: BpeVars,
    ): Promise<BpeVars> {
        const [locationDetails] = await Promise.all([BpeService.getConditionalLocations(eventData, templates)]);
        return vars.rebuildWith({ locationDetails });
    }

    public static async getConditionalLocations(eventData: EventData, templates: string[]) {
        // Only fetch additional info if location variables are used
        const match = /\$pro.vars.(pre)?(pref)?(locations|occurrencesLocations|buildings|locationFeatures)/i;
        if (!templates.some((template) => match.test(template))) return;

        const fa = S25Util.array.forceArray;
        const reservations = fa(eventData?.profile).flatMap((profile) => fa(profile.reservation));
        const spaceReservations = reservations.flatMap((res) => fa(res.space_reservation));
        const draftSpaceReservations = reservations.flatMap((res) => fa(res.draft_space_reservation));
        const assignedSpaceIds = spaceReservations.map((space) => space.space_id);
        const draftSpaceIds = draftSpaceReservations.map((space) => space.draft_space_id);
        const spaceIds = S25Util.array.unique(assignedSpaceIds.concat(draftSpaceIds));

        const spaceDetails = await SpaceService.getSpacesIncludes(spaceIds, ["features"]);
        return new Map(spaceDetails.map((space) => [space.space_id, space]));
    }

    public static async sendTemplate(
        data: BpeSendEmailData & {
            template: EmailTemplate;
            scenario: Partial<
                Pick<EmailScenario, "itemId" | "itemName" | "isScheduled" | "scheduleType" | "scheduleDays">
            >;
        },
    ) {
        const { template, scenario, variables, eventData, eventId, sessionId, reports, contracts, fls } = data;
        const templateCode = template.mode === "code" ? template.code : BpeUtil.templateToCode(template);
        variables.scenario = scenario.itemName;
        variables.template = template.itemName;
        const finalTemplateCode = `{${BpeUtil.interpolateString(variables, templateCode)}}`;
        const email = BpeService.executeEmailTemplate(finalTemplateCode, variables, eventData);
        let webServiceFiles: string[];
        if (parseInt(email.iCalFile) === 1) {
            webServiceFiles = [DataAccess.getUrl(`/event.ics?event_id=${eventId}&session_id=${sessionId}`)];
        }

        await BpeService.populateDMReports({ reports, contracts, email, eventId, variables });

        const reportRights = hasReadFls(fls.REP_LIST);
        return EmailService.sendEmailFromClass(
            email,
            eventId,
            1,
            reportRights,
            webServiceFiles,
            template.itemId,
            scenario,
        );
    }

    /**
     * triggers a put notification from a scenario, we do not allow templates at this time so scenarios and eventData are currently unused
     * @param scenarios
     * @param variables
     * @param eventData
     */
    public static async sendPushNotify(
        scenarios: EmailScenario[],
        variables: BpeVarsCombined,
        eventData: any,
    ): Promise<void> {
        scenarios = S25Util.array.unique(scenarios, (s) => s.itemId);
        const createdTasks = variables.taskActions?.create || [];
        if (createdTasks?.length > 0) {
            return PushNotifyService.notify(
                "New Task",
                "A task has been assigned to you",
                createdTasks.map((task) => task.id),
            );
        }
    }

    public static async getEventBillItems(eventId: number) {
        const pricing = await PricingService.getStandardPricingForEventIncludeRateInfo([eventId]);
        if (!pricing?.data?.items?.[0]?.billing) return { billItems: [], pricing };

        const { data, expandedInfo } = pricing;
        const { lineItems, totals, adjustments } = data.items[0].billing;

        const rateGroups: Record<number, string> = {};
        const rateSchedules: Record<number, string> = {};
        for (let group of expandedInfo.rateGroups) rateGroups[group.rateGroup_id] = group.rateGroupName;
        for (let schedule of expandedInfo.rateSchedules) rateSchedules[schedule.rateId] = schedule.rateName;

        // Line Items
        const billItems: BpeBillItem[] = (lineItems || [])
            .map((item) => ({
                ...item,
                // Add rate group and rate schedule names
                rateGroupName: rateGroups[item.rateGroupId] || "",
                rateScheduleName: rateSchedules[item.rateId] || "",
            }))
            .reverse(); // Reverse so that spaces come first

        // Subtotal
        billItems.push({
            itemName: "Subtotal",
            listPrice: totals.occurrenceListPrice,
            adjustmentAmt: totals.taxableAmount - totals.occurrenceListPrice,
            price: totals.taxableAmount,
            tax: totals.tax,
            total: totals.taxableAmount + totals.tax,
        });

        // End Adjustments
        Array.prototype.push.apply(
            billItems,
            (adjustments || []).map((adj) => ({ itemName: "Adjustment", ...adj })),
        );

        // Grand Total
        billItems.push({
            itemName: "Total",
            listPrice: totals.occurrenceListPrice,
            adjustmentAmt: totals.grandTotal - totals.occurrenceListPrice - totals.tax,
            price: totals.occurrenceListPrice + (totals.grandTotal - totals.occurrenceListPrice - totals.tax),
            tax: totals.tax,
            total: totals.grandTotal,
        });

        return { billItems, pricing };
    }

    @Timeout
    public static async getEventData(
        eventID: number | NumericalString,
        cache = false,
        longCache = false,
        include?: EventIncludeOption[],
    ) {
        const includes: EventIncludeOption[] = [
            "customers",
            "reservations",
            "attributes",
            "workflow",
            "workflow_history",
            "text",
            "requirements",
            "categories",
            "relationships",
            "draft",
        ];
        if (include) includes.push(...include);

        const FLS = await FlsService.getFls();
        const billing =
            FLS.EVENT_BILLING === AccessLevels.None ? undefined : BpeService.getEventBillItems(Number(eventID));

        let eventData: ReturnType<typeof EventService.getEventInclude>;
        if (cache && longCache)
            eventData = EventService.getEventIncludeCachedLong(eventID as number, includes, null, true);
        else if (cache) eventData = EventService.getEventIncludeCached(eventID as number, includes, null, true);
        else eventData = EventService.getEventInclude(eventID as number, includes, null, true);

        return Promise.all([billing, eventData]).then(([billing, eventData]) => {
            if (!eventData) return;

            eventData.bill_items = billing?.billItems;
            eventData.pricing = billing?.pricing;
            return eventData;
        });
    }

    ////////////////////
    //source: "event-form", "express",
    @Timeout
    public static async bpeCreateToDo(scenarios: EmailScenario[], variables: BpeVars, eventData: any) {
        if (!eventData.todo && !scenarios.length) return; // new event haven't create toDo yet
        scenarios = S25Util.array.unique(scenarios, (s) => s.itemId);
        let eventId = parseInt(S25Util.propertyGetVal(eventData, "event_id"));

        const fls = await FlsService.getFls();

        if (!hasFullFls(fls.CREATE_TODO)) return;

        for (const scenario of scenarios) {
            for (const template of scenario?.todoTemplates || []) {
                let templateCode = template.mode === "code" ? template.code : BpeUtil.todoTemplateToCode(template);
                variables.template = template.itemName;
                let finalTemplateCode = `{${BpeUtil.interpolateString(variables, templateCode)}}`;
                const todo = BpeService.executeTodoTemplate(finalTemplateCode, variables, eventData);
                const dueDateOffset: NumericalString = todo?.dueDate || "0";
                /******* check todo has been create or not *** */
                let taskName: any = todo.taskName;
                let assignTo: number = parseInt(todo.assignTo);

                if (eventData?.todo) {
                    const exists = eventData?.todo.some(
                        (task: any) => task?.todo_name === taskName && task.cur_assigned_to_id === assignTo,
                    );
                    if (exists) continue;
                }

                const dueDate = S25Util.date.addDays(eventData.start_date, +dueDateOffset);

                let item = {
                    taskName: todo.taskName,
                    eventId: eventId,
                    taskComment: todo.comment,
                    dueDate: dueDate,
                    assignedById: +todo.assignBy,
                    assignedToId: +todo.assignTo,
                };

                TaskService.createEventTodo(item, String(template.itemName), true);
            }
        }
    }

    public static executeEmailTemplate(templateCode: string, vars: any, eventData: any): EmailClass {
        const email = ExecutionEnvironment.run(templateCode, {
            vars,
            eventData: eventData,
            util: BpeUtil.util,
        });
        email.reports =
            email?.reports
                ?.split(",")
                .map(S25Util.trim)
                .filter((r: unknown) => r) || [];
        return email;
    }

    public static executeTodoTemplate(templateCode: string, vars: any, eventData: any): TodoClass {
        const todo = ExecutionEnvironment.run(templateCode, {
            vars,
            eventData: eventData,
            util: BpeUtil.util,
        });
        return todo;
    }

    public static async populateDMReports(data: {
        reports: Report.Object[];
        contracts: DocumentListItem[];
        email: EmailClass;
        eventId: number;
        variables?: Bpe.BpeVars;
    }) {
        const { reports, contracts, email, eventId, variables } = data;
        if (!reports) return;

        const documentReportPromises: Promise<EmailReport>[] = [];
        const reportsById = new Map(reports.map((report) => [Number(report.report_id), report]));
        const documentsByReportId = new Map(contracts.map((doc) => [doc.reportId, doc]));
        for (const reportId of email.reports) {
            const isString = reportId === "confirmation" || reportId === "invoice";
            const report = !isString && reportsById.get(Number(reportId));

            if (report && report.report_engine === "DM") {
                const document = documentsByReportId.get(Number(report.report_id));
                if (!document) continue;

                const idPromise = DocumentService.getEventDocumentRunId(document.itemId, eventId, variables);
                const docPromise: Promise<EmailReport> = idPromise.then((id) => ({
                    itemID: id,
                    rpt_id: reportId as string,
                    rpt_engine: "DM",
                    isString: false,
                    objString: "",
                    fromTemplate: true,
                }));
                documentReportPromises.push(docPromise);
            } else {
                documentReportPromises.push(
                    Promise.resolve({
                        itemID: null,
                        rpt_id: isString ? null : parseInt(reportId as string),
                        rpt_engine: "",
                        isString: isString,
                        objString: reportId as string,
                        fromTemplate: true,
                    }),
                );
            }
        }

        email.reports = await Promise.all(documentReportPromises);
    }
}

function findTodos(eventData: any) {
    let todos = S25Util.propertyGet(eventData, "todo") || [];
    todos = S25Util.array.forceArray(todos);
    todos = todos.filter((todo: any) => {
        return todo.todo_subtype === 99;
    });

    return todos;
}

export interface EmailFullScenariosResponse {
    root: {
        scenarios: EmailScenario[];
    };
}
export type TaskTriggers = { assignments: boolean; approvals: boolean; fyis: boolean; todos?: boolean };
export type TaskActions = {
    taskTypes: Task.IdName[];
    actions: Bpe.Tasks.Action[];
};

export interface EmailScenario {
    itemId: number;
    itemName: string;
    description: string;
    mode: "form" | "code" | "hybridForm" | "hybridCode";
    code: string;
    isActive: NumericalBoolean;
    templates?: EmailTemplate[];
    createToDo?: NumericalBoolean;
    pushNotify?: NumericalBoolean;
    todoTemplates?: ToDoTemplate[];
    codeData?: {
        on: "all" | "edit" | "create";
        sources: {
            task?: boolean;
            "event-form"?: boolean;
            express?: boolean;
            "event-state-change"?: boolean;
            cancelTodos?: boolean; //Event cancelation request
            createTodo?: boolean; //more action menu
        };
        taskTriggers: TaskTriggers;
        taskActions: TaskActions;
        pre: ScenarioCriteria;
        post: ScenarioCriteria;
    };
    // Below properties are no longer used. This data is now stored in code
    onAction?: "all" | "edit" | "create";
    inclLocPref?: NumericalBoolean;
    inclResPref?: NumericalBoolean;
    onEventStateChangeAction?: NumericalBoolean;
    onEventFormAction?: NumericalBoolean;
    onTaskAction?: NumericalBoolean;
    onTodoAction?: NumericalBoolean;
    onExpressAction?: NumericalBoolean;
    eventStates?: ScenarioItem[];
    resources?: ScenarioItem[];
    eventTypes?: ScenarioItem[];
    locations?: ScenarioItem[];
    requirements?: ScenarioItem[];
    preEventStates?: ScenarioItem[];
    organizations?: ScenarioItem[];
    securityGroups?: ScenarioItem[];
    attributes?: ScenarioAttributeItem[];
    allApDone?: NumericalBoolean;
    allNpDone?: NumericalBoolean;
    allFyiDone?: NumericalBoolean;
    allTodoDone?: NumericalBoolean;
    isScheduled?: NumericalBoolean;
    scheduleType?: "daysFromEventStart" | "daysFromEventEnd" | "daysFromNow";
    scheduleDays?: number;
}

export type ScenarioCriteria = {
    usePrefLocations?: boolean;
    usePrefResources?: boolean;
    states: ScenarioIncludeExclude<ScenarioItem>;
    types: ScenarioIncludeExclude<ScenarioItem>;
    locations: ScenarioIncludeExclude<ScenarioItem>;
    resources: ScenarioIncludeExclude<ScenarioItem>;
    primaryOrgs: ScenarioIncludeExclude<ScenarioItem>;
    requirements: ScenarioIncludeExclude<ScenarioItem>;
    securityGroups: ScenarioIncludeExclude<ScenarioItem>;
    customAttributes: ScenarioIncludeExclude<ScenarioAttributeItem>;
    expectedHeadcount: { operator: string; value?: number; value2?: number };
};

type ScenarioIncludeExclude<T extends ScenarioItem | ScenarioAttributeItem> = {
    include: T[];
    exclude: T[];
};

export interface ScenarioItem {
    itemId: number;
    itemName: string;
}

export interface ScenarioAttributeItem {
    itemId: number;
    itemName: string;
    itemValue: any;
    additionalInfo: string;
    custAtrbType: CustomAttributes.Type;
    operator: Rules.Operator;
}

export interface EmailFullTemplatesResponse {
    root: {
        templates: EmailTemplate[];
    };
}
export interface ToDoFullTemplatesResponse {
    root: {
        templates: ToDoTemplate[];
    };
}

export interface BPETemplate {
    mode: "form" | "code";
    itemId: number;
    itemName?: string | number;
    code: string;
    comment: string;
    isToDoTemplate?: NumericalBoolean;
    tags?: MdTag[];
}

export interface EmailTemplate extends BPETemplate {
    mode: "form" | "code";
    cc: string;
    itemId: number;
    reports: string; // Comma separated string
    itemName: string | number;
    bcc: string;
    code: string;
    subject: string;
    isManual: NumericalBoolean;
    to: string;
    body: string;
    from?: string;
    isToDoTemplate?: NumericalBoolean;
    iCalFile?: NumericalBoolean;
    tags?: MdTag[];
}

export interface ToDoTemplate extends BPETemplate {
    mode: "form" | "code";
    itemId: number;
    itemName?: string | number;
    code: string;
    comment: string;
    assignBy: any;
    assignTo: any;
    dueDate?: number;
    isToDoTemplate?: NumericalBoolean;
    taskName?: string;
    multiSelectBeanAssignTo?: any;
    multiSelectBeanAssignBy?: any;
}

export type BpeBillItem = Partial<Total & LineItem & Adjustment> & {
    rateGroupName?: string;
    rateScheduleName?: string;
};

type BpeSendEmailData = {
    eventData: any;
    eventId: number;
    variables: BpeVars;
    fls: Fls;
    sessionId: string;
    reports: Report.Object[];
    contracts: DocumentListItem[];
};

type RunScenariosData<T> = {
    getSources: (preEventData: EventData, postEventData: EventData) => BpeScenarioTypes[];
    silent?: boolean;
    eventId: number | Promise<number>;
    change: () => Promise<T>;
    // Validate against postEventData wheter we really want to run scenarios
    updateScheduledEmails?: boolean;
};
